import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import {
  formatCustomUnitMeasurementEquivalenceSuffix,
  getRecipeIngredientsBlocker,
  isRecipeType,
  parseUnitToShow,
  reCalculateMacros,
} from "../../../../../utils/nutrition-util";
import { truncateText } from "../../../../../utils/string-helpers";

export const FoodItems = ({
  items,
  onRemoveItem,
  onUpdateItems,
  recipeIngredients = {},
  recipeIngredientsSupported = false,
  onEditIngredients,
}) => {
  const { t } = useTranslation("athlete-portal");
  const [editingIndex, setEditingIndex] = useState(null);
  const inputRefs = useRef({});

  /**
   * Focus the input clicking the span
   */
  useEffect(() => {
    if (editingIndex !== null && inputRefs.current[editingIndex]) {
      inputRefs.current[editingIndex].focus();
    }
  }, [editingIndex]);

  /**
   * Not use input + span, just use span
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        editingIndex !== null &&
        inputRefs.current[editingIndex] &&
        !inputRefs.current[editingIndex].contains(event.target)
      ) {
        setEditingIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingIndex]);

  /**
   * Clean the not used refs
   */
  useEffect(() => {
    return () => {
      inputRefs.current = {};
    };
  }, []);

  /**
   * Function to increase or decrease de quantity number
   * @param {*} index
   * @param {*} newQuantity
   */
  const updateQuantity = (index, newQuantity) => {
    const updatedItem = {
      customQuantityNumber: newQuantity
        ? Math.max(0.1, Math.round(Number(newQuantity) * 100) / 100)
        : "",
    };
    onUpdateItems(index, updatedItem);
  };

  /**
   * Avoid strings in number input
   * @param {*} e
   */
  const handleKeyDown = (e) => {
    if (["e", "E", "-", "+", ","].includes(e.key)) {
      e.preventDefault();
    }
  };

  return items.map((item, index) => {
    const {
      _id,
      customQuantityNumber = 1,
      quantityNumber = 1,
      quantityDescription = "Servings",
      name,
      proteins: itemProteins,
      carbs: itemCarbs,
      fats: itemFats,
      calories: itemCalories,
      unitOfMeasure = "g",
      customUnitOfMeasure = "custom",
      measurementsValue,
      type,
      skipRecalculation,
      macrosScaledToQuantity,
      brand,
      foodIndex,
    } = item;
    // Only modals that handle ingredient editing (the Food modal) get the button;
    // the Edit Meal modal reuses these rows without it
    const showEditIngredients =
      type === "external_recipe" && Boolean(onEditIngredients);
    const ingredientsBlocker = showEditIngredients
      ? getRecipeIngredientsBlocker(
          item,
          recipeIngredients[foodIndex],
          recipeIngredientsSupported
        )
      : null;
    const itemDetails = {
      itemProteins,
      itemCarbs,
      itemFats,
      itemCalories,
      foodKey: "foodId",
      customQuantityNumber,
      quantityNumber,
      customUnitOfMeasure,
      unitOfMeasure,
      measurementsValue,
      type,
      skipRecalculation,
      macrosScaledToQuantity,
      ...(isRecipeType(type) && { recipeMacrosBase: quantityDescription }),
    };

    const { proteins, carbs, fats, calories } = reCalculateMacros({
      itemDetails,
    });
    /**
     * Suffix for meal-plan UI when row unit is "custom": scaled catalog measurement (same scale as
     * reCalculateMacros custom branch: (mealQty / foodId.quantityNumber) * measurementsValue).
     * Uses foodId.unitOfMeasure as displayed (no translation).
     * @param {number|string} mealQuantityNumber
     * @param {string} [mealUnitOfMeasure]
     * @param {{ quantityNumber?: number|string, measurementsValue?: number|string, unitOfMeasure?: string }|null|undefined} foodId
     * @returns {string} e.g. " (100 g)" or ""
     */
    const measurementEquivSuffix = formatCustomUnitMeasurementEquivalenceSuffix(
      customQuantityNumber,
      customUnitOfMeasure,
      {
        quantityNumber,
        measurementsValue,
        unitOfMeasure,
      }
    );

    const quantityViewMainText = `${customQuantityNumber} ${parseUnitToShow(
      customQuantityNumber,
      type,
      customUnitOfMeasure,
      quantityDescription
    )}`;
    const quantityViewFullText = `${quantityViewMainText}${measurementEquivSuffix}`;
    /** Whole quantity row (span + chevron buttons): layout when text needs more space */
    const quantityRowLong = quantityViewFullText.length > 12;

    const renderMeasurementEquivSuffix = () =>
      measurementEquivSuffix ? (
        <span className="measurement-equiv-suffix">
          {measurementEquivSuffix}
        </span>
      ) : null;

    return (
      <div key={`${_id}-${index}`} className={`content`}>
        <div className="info-food">
          <div>
            <span>
              {brand && !name.toLowerCase().includes(brand.toLowerCase())
                ? `${name} (${brand})`
                : name}
            </span>
          </div>
          <div className="nutrition-info">
            <span>
              {t("foodItems.protein")} {proteins}g{" "}
            </span>
            <span>&#8226;</span>
            <span>
              {t("foodItems.carbs")} {carbs}g
            </span>
            <span>&#8226;</span>
            <span>
              {t("foodItems.fat")} {fats}g
            </span>
            <span>&#8226;</span>
            <span>
              {t("foodItems.calories")} {calories}
            </span>
          </div>
          {showEditIngredients && (
            <button
              type="button"
              data-button-type="text"
              className="h-auto! px-0! text-xs"
              disabled={Boolean(ingredientsBlocker)}
              onClick={() => onEditIngredients(foodIndex)}
              title={
                ingredientsBlocker
                  ? t(`foodItems.ingredientsBlocker.${ingredientsBlocker}`)
                  : undefined
              }
            >
              {/* span keeps the label above the light-theme hover background */}
              <span>{t("foodItems.editIngredients")}</span>
            </button>
          )}
        </div>
        <div className="actions">
          <div
            className={`number-container${quantityRowLong ? " number-container--long" : ""}`}
          >
            <button
              onClick={() =>
                updateQuantity(index, Number(customQuantityNumber) - 1)
              }
            >
              <SvgIcon src="/img/svg/angle-down.svg" />
            </button>
            {editingIndex === index ? (
              <div className="flex items-center">
                <input
                  ref={(el) => (inputRefs.current[index] = el)}
                  min={0.1}
                  type="number"
                  value={customQuantityNumber || ""}
                  onKeyDown={(e) => {
                    handleKeyDown(e);
                    if (e.key === "Enter") setEditingIndex(null);
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    //Allow only 2 decimals
                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                      updateQuantity(index, value);
                    }
                  }}
                />
                <span className="span-edit">
                  {truncateText(
                    parseUnitToShow(
                      customQuantityNumber,
                      type,
                      customUnitOfMeasure,
                      quantityDescription
                    ),
                    24
                  )}
                  {renderMeasurementEquivSuffix()}
                </span>
              </div>
            ) : (
              <span
                className="span-view"
                onClick={() => setEditingIndex(index)}
              >
                {truncateText(quantityViewMainText, 24)}
                {renderMeasurementEquivSuffix()}
              </span>
            )}
            <button
              onClick={() =>
                updateQuantity(index, Number(customQuantityNumber) + 1)
              }
            >
              <SvgIcon src="/img/svg/angle-up.svg" />
            </button>
          </div>
          <button onClick={() => onRemoveItem(index)}>
            <img src="/img/new-icons/red-cross.svg" />
          </button>
        </div>
      </div>
    );
  });
};

FoodItems.propTypes = {
  items: PropTypes.array,
  handleRemoveItem: PropTypes.func,
  onUpdateItems: PropTypes.func,
  recipeIngredients: PropTypes.object,
  recipeIngredientsSupported: PropTypes.bool,
  onEditIngredients: PropTypes.func,
};
